import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../../components/Navbar';
import { createAllPhaseMenuGroups } from '../../../utils/createAllPhaseMenuGroups';
import axiosInstance from '../../../utils/axiosInstance';
import config from '../../../config.json';
import {
  Book as BookIcon,
  Person as PersonIcon,
  School as ClassIcon,
  Settings as PrepSettingsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ContentCopy as CopyIcon,
  Home as PrepHomeIcon,
  Search as SearchIcon,
  Send as SendIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import TopBannerGuidance from '../../../components/TopBannerGuidance';
import OwlMascot from '../../../assets/貓頭鷹博士_雙手.gif';
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Box,
  Grid,
  IconButton,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  Snackbar,
  Menu,
  ListItemIcon as MenuListItemIcon,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress
} from '@mui/material';

export default function LessonPackageManagement() {
  const navigate = useNavigate();
  const [lessonPackages, setLessonPackages] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true); // 只用於第一次進頁
  const [refreshing, setRefreshing] = useState(false); // 背景刷新用
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [showOwlGuidance, setShowOwlGuidance] = useState(true);
  const [packageTaskCounts, setPackageTaskCounts] = useState({});
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);

  // 獲取備課包列表
  const fetchLessonPackages = async (force = false, opts = { background: false }) => {
    const now = Date.now();
    // 如果不是強制刷新，且距離上次刷新不到5秒，則不刷新
    if (!force && now - lastFetchTime < 5000) {
      return;
    }

    try {
      // 根據選項決定使用哪個 loading 狀態
      if (opts.background) {
        setRefreshing(true);
      } else {
        // 只有「第一次」才把整頁變成 loading 畫面
        if (initialLoading) {
          setInitialLoading(true);
        } else {
          setRefreshing(true);
        }
      }

      const response = await axiosInstance.get(config.getLessonPackages);
      if (response.data.success) {
        const packages = response.data.data || [];
        setLessonPackages(packages);
        setLastFetchTime(now);
        
        // 並行獲取每個備課包的任務數量
        const taskCountPromises = packages.map(async (pkg) => {
          try {
            const taskResponse = await axiosInstance.get(`${config.getLessonPackages}/${pkg.id}`);
            if (taskResponse.data.success) {
              const tasks = taskResponse.data.data?.LessonPackageTasks || [];
              return {
                packageId: pkg.id,
                counts: {
                  total: tasks.length,
                  hasContent: tasks.filter(t => t.title && t.content).length,
                  empty: tasks.filter(t => !t.title || !t.content).length
                }
              };
            }
          } catch (err) {
            console.error(`獲取備課包 ${pkg.id} 任務數量錯誤:`, err);
          }
          return {
            packageId: pkg.id,
            counts: { total: 0, hasContent: 0, empty: 0 }
          };
        });
        
        const taskCountResults = await Promise.all(taskCountPromises);
        const taskCounts = {};
        taskCountResults.forEach(result => {
          if (result) {
            taskCounts[result.packageId] = result.counts;
          }
        });
        setPackageTaskCounts(taskCounts);
      } else {
        setError('獲取備課包失敗');
      }
    } catch (error) {
      console.error('獲取備課包錯誤:', error);
      setError('獲取備課包失敗');
    } finally {
      setRefreshing(false);
      setInitialLoading(false);
    }
  };

  // 刪除備課包
  const handleDeleteLessonPackage = async (packageId) => {
    try {
      const response = await axiosInstance.delete(`${config.deleteLessonPackage}${packageId}`);
      if (response.data.success) {
        setSnackbar({ open: true, message: '備課包已刪除', severity: 'success' });
        fetchLessonPackages(true); // 刪除後強制重新獲取列表
      } else {
        setSnackbar({ open: true, message: '刪除失敗', severity: 'error' });
      }
    } catch (error) {
      console.error('刪除備課包錯誤:', error);
      setSnackbar({ open: true, message: '刪除失敗', severity: 'error' });
    }
  };

  // 處理選單開啟
  const handleMenuOpen = (event, packageId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedPackageId(packageId);
  };

  // 處理選單關閉
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPackageId(null);
  };

  // 處理刪除確認
  const handleDeleteConfirm = () => {
    if (selectedPackageId && window.confirm('確定要刪除這個備課包嗎？')) {
      handleDeleteLessonPackage(selectedPackageId);
    }
    handleMenuClose();
  };

  useEffect(() => {
    fetchLessonPackages(true); // 初始載入強制刷新
  }, []);

  // 頁面聚焦時刷新數據（但 Dialog 開著時不刷新，避免 unmount）
  useEffect(() => {
    const handleFocus = () => {
      // Dialog 開著就不要刷新整頁，避免 unmount
      if (openTaskDialog) {
        console.log('[LessonPackageManagement] Dialog 開著，跳過 focus 刷新');
        return;
      }
      fetchLessonPackages(false, { background: true }); // 背景刷新
    };

    const handleVisibilityChange = () => {
      if (document.hidden) return;
      // Dialog 開著就不要刷新整頁，避免 unmount
      if (openTaskDialog) {
        console.log('[LessonPackageManagement] Dialog 開著，跳過 visibilitychange 刷新');
        return;
      }
      fetchLessonPackages(false, { background: true }); // 背景刷新
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [openTaskDialog, lastFetchTime]);

  // A-3: 使用分層架構選單
  const allPhaseMenuGroups = createAllPhaseMenuGroups('preparation', 'lesson-packages', navigate);


  const filteredLessonPackages = lessonPackages.filter(pkg => {
    const matchesSearch = pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (pkg.description && pkg.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });


  // 只有首次載入時才顯示全頁 loading，背景刷新不卸載頁面
  if (initialLoading) {
    return (
      <div className="teacher-layout lesson-package-management-container">
        <div className="main-content-v2">
          <Navbar 
            allPhaseMenuGroups={allPhaseMenuGroups}
            currentPhase="preparation"
            onPhaseChange={(phase) => {
              if (phase === 'preparation') {
                navigate('/teacher/preparation');
              } else if (phase === 'implementation') {
                navigate('/teacher/implementation');
              } else if (phase === 'diagnosis') {
                navigate('/teacher/diagnosis');
              }
            }}
          />
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Typography>載入中...</Typography>
          </Box>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="teacher-layout lesson-package-management-container">
        <div className="main-content-v2">
          <Navbar 
            allPhaseMenuGroups={allPhaseMenuGroups}
            currentPhase="preparation"
            onPhaseChange={(phase) => {
              if (phase === 'preparation') {
                navigate('/teacher/preparation');
              } else if (phase === 'implementation') {
                navigate('/teacher/implementation');
              } else if (phase === 'diagnosis') {
                navigate('/teacher/diagnosis');
              }
            }}
          />
          <Alert severity="error" action={
            <Button color="inherit" size="small" onClick={fetchLessonPackages}>
              重新載入
            </Button>
          }>
            {error}
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="teacher-layout lesson-package-management-container">
      <div className="main-content-v2">
        <Navbar 
          allPhaseMenuGroups={allPhaseMenuGroups}
          currentPhase="preparation"
          onPhaseChange={(phase) => {
            if (phase === 'preparation') {
              navigate('/teacher/preparation');
            } else if (phase === 'implementation') {
              navigate('/teacher/implementation');
            } else if (phase === 'diagnosis') {
              navigate('/teacher/diagnosis');
            }
          }}
        />
        
        <div className="class-header-row">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <h1 className="main-title">備課包管理</h1>
            {/* 背景刷新指示器 */}
            {refreshing && (
              <CircularProgress size={20} sx={{ color: '#2b687f' }} />
            )}
          </Box>
          {/* 篩選區域在標題下方置中 */}
        <div className="filter-container">
          <TextField
            placeholder="🔍 搜尋備課包"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            className="filter-search-input"
          />
          <button
            className="btn-secondary filter-clear-btn"
            onClick={() => {
              setSearchTerm('');
            }}
          >
            清除篩選
          </button>
          </div>
        </div>

        {/* 頂部橫幅提醒 */}
        <TopBannerGuidance
          show={showOwlGuidance}
          onClose={() => setShowOwlGuidance(false)}
          severity={(() => {
            const totalPackages = lessonPackages.length;
            const packagesWithTasks = Object.values(packageTaskCounts).filter(count => count.total > 0).length;
            const packagesWithCompleteTasks = Object.values(packageTaskCounts).filter(
              count => count.total > 0 && count.empty === 0
            ).length;
            
            if (totalPackages === 0 || packagesWithTasks === 0) return 'info';
            if (packagesWithCompleteTasks < packagesWithTasks) return 'warning';
            return 'success';
          })()}
          mascotImage={OwlMascot}
          message={(() => {
            const totalPackages = lessonPackages.length;
            const packagesWithTasks = Object.values(packageTaskCounts).filter(count => count.total > 0).length;
            const packagesWithCompleteTasks = Object.values(packageTaskCounts).filter(
              count => count.total > 0 && count.empty === 0
            ).length;
            
            if (totalPackages === 0) {
              return '先建立備課包，然後在「任務編輯」頁面中編輯任務內容，最後再進行課程派發。';
            } else if (packagesWithTasks === 0) {
              return '記得先點擊「任務編輯」頁面，在任務清單中建立和編輯任務，確認任務內容完整後再進行課程派發。';
            } else if (packagesWithCompleteTasks < packagesWithTasks) {
              return `目前有 ${packagesWithTasks} 個備課包有任務，但部分任務內容還不完整。建議先完成任務編輯，確保標題和描述都填寫好，再進行課程派發。`;
            } else {
              return '很好！備課包任務都已經編輯完成。現在可以選擇備課包進行課程派發了。';
            }
          })()}
        />

        {/* 備課包列表 */}
        <div className="activity-card-list grid-responsive">
          {/* 建立備課包卡片 */}
          <div
            className="activity-card add-activity"
            onClick={() => setOpenCreateDialog(true)}
            style={{ 
              background: '#2b687f',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              minHeight: '200px',
              maxHeight: '200px',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.02)';
              e.target.style.transition = 'all 0.3s ease';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.transition = 'all 0.3s ease';
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <AddIcon sx={{ fontSize: 48, mb: 1 }} />
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                建立備課包
              </div>
            </div>
          </div>

          {/* 備課包卡片 */}
          {filteredLessonPackages.map((pkg) => (
            <div
              className="activity-card"
              key={pkg.id}
              style={{ 
                position: 'relative', 
                background:'#fff', 
                boxShadow:'0 2px 8px rgba(44,44,80,0.08)', 
                borderRadius: '12px', 
                border:'1.5px solid #e0e0e0',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                minHeight: '200px',
                maxHeight: '200px',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(44,44,80,0.12)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(44,44,80,0.08)';
              }}
            >
              <div className="activity-name" style={{ fontSize: '1.2rem', marginBottom: '8px' }}>
                        {pkg.name}
              </div>
              
              <div className="activity-description" style={{ 
                fontSize: '0.8rem', 
                color: '#666', 
                marginBottom: '8px',
                padding: '0 8px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}>
                    {pkg.description || '暫無描述'}
              </div>

              {/* 備課包資訊 */}
              <div style={{ 
                display: 'flex', 
                gap: '6px', 
                marginBottom: '8px',
                padding: '0 8px',
                flexWrap: 'wrap'
              }}>
                {pkg.subject && (
                  <span style={{
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    fontSize: '0.7rem',
                    fontWeight: '500'
                  }}>
                    {pkg.subject}
                  </span>
                )}
                {pkg.grade && (
                  <span style={{
                    backgroundColor: '#f3e5f5',
                    color: '#7b1fa2',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    fontSize: '0.7rem',
                    fontWeight: '500'
                  }}>
                    {pkg.grade}
                  </span>
                )}
                <span style={{
                  backgroundColor: pkg.status === 'completed' ? '#e8f5e8' : pkg.status === 'published' ? '#e3f2fd' : '#fff3e0',
                  color: pkg.status === 'completed' ? '#2e7d32' : pkg.status === 'published' ? '#1976d2' : '#f57c00',
                  padding: '2px 6px',
                  borderRadius: '8px',
                  fontSize: '0.7rem',
                  fontWeight: '500'
                }}>
                  {pkg.status === 'completed' ? '完成' : pkg.status === 'published' ? '已發布' : pkg.status === 'preparing' ? '備課中' : '草稿'}
                </span>
              </div>

              {/* 主要功能按鈕 */}
              <div style={{ 
                display: 'flex', 
                gap: '6px', 
                justifyContent: 'center',
                marginTop: '8px',
                padding: '0 8px'
              }}>
                <button
                  className="btn-card"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPackage(pkg);
                    setOpenTaskDialog(true);
                  }}
                >
                  任務編輯
                </button>
                <button
                  className="btn-card"
                  onClick={(e) => {
                    e.stopPropagation();
                    // 派發課程 - 使用社群名+備課包名作為標題
                    navigate('/teacher/preparation/course-dispatch', { 
                      state: { 
                        lessonPackage: pkg,
                        dispatchTitle: `${pkg.name} - 派發課程`
                      } 
                    });
                  }}
                >
                  課程派發
                </button>
              </div>

              {/* 選項選單 */}
              <div
                className="activity-menu"
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  color: '#666',
                  zIndex: 1
                }}
                onClick={(e) => handleMenuOpen(e, pkg.id)}
              >
                ⋮
              </div>
            </div>
          ))}
        </div>

        {/* 建立備課包對話框 */}
        <CreateLessonPackageDialog
          open={openCreateDialog}
          onClose={() => setOpenCreateDialog(false)}
          onSuccess={(message) => {
            setSnackbar({ open: true, message, severity: 'success' });
            fetchLessonPackages(); // 重新獲取列表
          }}
        />

        {/* 編輯備課包對話框 */}
        <EditLessonPackageDialog
          open={openEditDialog}
          package={editingPackage}
          onClose={() => {
            setOpenEditDialog(false);
            setEditingPackage(null);
          }}
          onSuccess={(message) => {
            setSnackbar({ open: true, message, severity: 'success' });
            fetchLessonPackages(true); // 重新獲取列表
          }}
        />

        {/* 任務清單對話框 */}
        <TaskListDialog
          open={openTaskDialog}
          package={selectedPackage}
          onClose={() => {
            const packageToRefresh = selectedPackage; // 保存引用，因為 setSelectedPackage 會清空
            setOpenTaskDialog(false);
            setSelectedPackage(null);
            // 關閉對話框後重新獲取任務數量統計
            if (packageToRefresh) {
              fetchLessonPackages(true);
            }
          }}
          onSuccess={(message) => {
            setSnackbar({ open: true, message, severity: 'success' });
            // 不在 Dialog 打開時刷新，避免觸發全頁 loading 導致 Dialog 被卸載
            // 刷新將在 Dialog 關閉時進行（onClose 中）
          }}
        />

        {/* 選項選單 */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={() => {
            const pkg = lessonPackages.find(p => p.id === selectedPackageId);
            if (pkg) {
              setEditingPackage(pkg);
              setOpenEditDialog(true);
            }
            handleMenuClose();
          }}>
            <EditIcon sx={{ mr: 1 }} />
            編輯資訊
          </MenuItem>
          <MenuItem onClick={async () => {
            if (selectedPackageId && window.confirm('確定要複製這個備課包嗎？')) {
              try {
                const response = await axiosInstance.post(`${config.getLessonPackages}/${selectedPackageId}/copy`);
                if (response.data.success) {
                  setSnackbar({ open: true, message: '備課包複製成功', severity: 'success' });
                  fetchLessonPackages(true);
                } else {
                  setSnackbar({ open: true, message: '複製失敗', severity: 'error' });
                }
              } catch (error) {
                console.error('複製備課包錯誤:', error);
                setSnackbar({ open: true, message: '複製失敗', severity: 'error' });
              }
            }
            handleMenuClose();
          }}>
            <CopyIcon sx={{ mr: 1 }} />
            複製備課包
          </MenuItem>
          <MenuItem onClick={handleDeleteConfirm}>
            <DeleteIcon sx={{ mr: 1 }} />
            刪除備課包
          </MenuItem>
        </Menu>

        {/* 通知訊息 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

      </div>
    </div>
  );
}

// 任務清單對話框組件
function TaskListDialog({ open, package: packageData, onClose, onSuccess }) {
  const [editingTask, setEditingTask] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedUnits, setExpandedUnits] = useState({}); // 章節展開狀態

  // 用於追蹤是否正在添加新任務
  const addingTaskRef = React.useRef(null);
  
  // 用於滾動到任務元素的 ref
  const taskItemRefs = React.useRef({});
  
  // 定義 draft key（用 packageId 區分）
  const draftKey = packageData?.id ? `taskDraft:${packageData.id}` : null;
  
  // 當任務列表更新且正在編輯新添加的任務時，自動滾動
  React.useEffect(() => {
    if (!editingTask?.id || !addingTaskRef.current?.taskId) return;
    
    const editingId = Number(editingTask.id);
    const addingId = Number(addingTaskRef.current.taskId);
    
    // 只有當正在編輯的任務就是新添加的任務時，才執行滾動
    if (editingId !== addingId) return;
    
    // 檢查任務是否已經渲染到 DOM
    const taskElement = taskItemRefs.current[editingId];
    if (taskElement) {
      console.log('[useEffect] ✅ 檢測到新任務已渲染，自動滾動，任務ID:', editingId);
      // 使用雙重 requestAnimationFrame 確保 DOM 完全更新
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          taskElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      });
    }
  }, [tasks, editingTask?.id]); // 當任務列表或編輯任務 ID 變化時觸發
  
  // 驗證是否 unmount 的 log
  React.useEffect(() => {
    console.log('[TaskListDialog] ✅ mount');
    return () => {
      console.log('[TaskListDialog] ❌ unmount');
    };
  }, []);

  // 移除頁面可見性監聽，因為編輯狀態應該只在保存、取消或關閉對話框時清除
  // 不再監聽 visibilitychange，讓編輯狀態保持不變

  React.useEffect(() => {
    if (open && packageData) {
      // 如果正在添加任務，不重新獲取（避免重置狀態）
      if (!addingTaskRef.current) {
        // 如果正在編輯任務，保持編輯狀態（無論是否切換標籤頁）
        const shouldPreserve = !!editingTask;
        console.log('[useEffect] 對話框打開，editingTask:', editingTask, 'shouldPreserve:', shouldPreserve);
        fetchTasks(shouldPreserve);
      } else {
        console.log('[useEffect] 正在添加任務，跳過 fetchTasks，addingTaskRef:', addingTaskRef.current);
      }
    }
  }, [open, packageData]);

  // 編輯內容變動時自動保存到 sessionStorage
  React.useEffect(() => {
    if (!draftKey) return;
    if (!editingTask) {
      // 如果沒有編輯任務，清除 draft
      sessionStorage.removeItem(draftKey);
      return;
    }

    const draft = {
      editingTask,
      expandedUnits,
      savedAt: Date.now()
    };
    try {
      sessionStorage.setItem(draftKey, JSON.stringify(draft));
      console.log('[TaskListDialog] 已保存編輯狀態到 sessionStorage:', draftKey);
    } catch (e) {
      console.warn('[TaskListDialog] 保存編輯狀態失敗:', e);
    }
  }, [draftKey, editingTask, expandedUnits]);

  // Dialog 開啟時嘗試還原編輯狀態
  React.useEffect(() => {
    if (!open || !draftKey) return;

    try {
      const raw = sessionStorage.getItem(draftKey);
      if (!raw) return;

      const draft = JSON.parse(raw);
      if (draft?.editingTask?.id) {
        console.log('[TaskListDialog] 從 sessionStorage 還原編輯狀態:', draft);
        setEditingTask(draft.editingTask);
        if (draft.expandedUnits) {
          setExpandedUnits(draft.expandedUnits);
        }

        // 還原後把該章節展開
        const unitNum = draft.editingTask.unit_number;
        if (unitNum) {
          const unitKey = `unit_${unitNum}`;
          setExpandedUnits(prev => ({ ...prev, [unitKey]: true }));
        }
      }
    } catch (e) {
      console.warn('[TaskListDialog] 還原編輯狀態失敗，清除損壞的 draft:', e);
      sessionStorage.removeItem(draftKey);
    }
  }, [open, draftKey]);

  // 當對話框關閉時，清除編輯狀態和 draft
  React.useEffect(() => {
    if (!open) {
      console.log('[TaskListDialog] 對話框關閉，清除編輯狀態');
      if (draftKey) {
        sessionStorage.removeItem(draftKey);
      }
      setEditingTask(null);
      addingTaskRef.current = null;
    }
  }, [open, draftKey]);


  const fetchTasks = async (preserveEditingState = false) => {
    try {
      // 如果正在添加任務，不要設置 loading，避免觸發父組件重新渲染導致 Dialog 被卸載
      if (!addingTaskRef.current) {
        setLoading(true);
      }
      
      // 如果正在添加任務，不要清除編輯狀態
      // 重要：在保存 currentEditingTaskId 之前檢查，因為設置編輯任務是異步的
      if (addingTaskRef.current?.taskId) {
        preserveEditingState = true;
        console.log('[fetchTasks] ✅ 檢測到正在添加任務，保持編輯狀態，taskId:', addingTaskRef.current.taskId);
        console.log('[fetchTasks] 當前 editingTask:', editingTask);
        console.log('[fetchTasks] addingTaskRef.current:', addingTaskRef.current);
      } else {
        console.log('[fetchTasks] ⚠️ addingTaskRef.current?.taskId 不存在，addingTaskRef.current:', addingTaskRef.current);
      }
      
      // 保存當前正在編輯的任務 ID 和完整狀態（如果有的話）
      const currentEditingTaskId = editingTask?.id;
      const currentEditingTask = editingTask ? { ...editingTask } : null;
      
      console.log('[fetchTasks] 開始獲取任務列表，preserveEditingState:', preserveEditingState, 'currentEditingTaskId:', currentEditingTaskId);
      const response = await axiosInstance.get(`${config.getLessonPackages}/${packageData.id}`);
      if (response.data.success) {
        const updatedPackage = response.data.data;
        const fetchedTasks = updatedPackage.LessonPackageTasks || [];
        
        // 按 unit_number 和 subtask_number 排序（優先），然後按 order_index
        const sortedTasks = [...fetchedTasks].sort((a, b) => {
          // 優先按章節號排序
          const aUnit = a.unit_number ?? 1;
          const bUnit = b.unit_number ?? 1;
          if (aUnit !== bUnit) {
            return aUnit - bUnit;
          }
          
          // 同章節內按子任務號排序（整數）
          const aSubtask = typeof a.subtask_number === 'number' ? a.subtask_number : (parseInt(a.subtask_number) || 0);
          const bSubtask = typeof b.subtask_number === 'number' ? b.subtask_number : (parseInt(b.subtask_number) || 0);
          return aSubtask - bSubtask;
        });
        setTasks(sortedTasks);
        
        // 如果正在添加任務，且新任務已找到，不要清除編輯狀態（讓 handleAddTask 自己設置）
        // 重要：這個檢查必須在設置編輯狀態之前執行，並且要直接返回，不執行後續邏輯
        if (addingTaskRef.current?.taskId) {
          const newTaskId = addingTaskRef.current.taskId;
          console.log('[fetchTasks] 🔍 檢測到正在添加任務，taskId:', newTaskId);
          console.log('[fetchTasks] addingTaskRef.current:', addingTaskRef.current);
          console.log('[fetchTasks] 當前 editingTask:', editingTask);
          
          const newTask = sortedTasks.find(t => Number(t.id) === Number(newTaskId));
          if (newTask) {
            console.log('[fetchTasks] ✅ 找到新添加的任務，不清除編輯狀態，讓 handleAddTask 設置:', newTask.id);
            console.log('[fetchTasks] 當前 editingTask?.id:', editingTask?.id);
            console.log('[fetchTasks] 新任務ID:', newTaskId);
            
            // 如果編輯狀態已經設置，保持不變
            if (editingTask && Number(editingTask.id) === Number(newTaskId)) {
              console.log('[fetchTasks] ✅ 編輯狀態已設置，保持不變，不清除');
              // 不調用 setEditingTask(null)，直接返回
              return sortedTasks;
            }
            // 如果編輯狀態還沒設置，也不清除（讓 handleAddTask 設置）
            console.log('[fetchTasks] 編輯狀態尚未設置，不清除，讓 handleAddTask 設置，直接返回');
            // 重要：不要執行後續的編輯狀態清除邏輯，直接返回
            return sortedTasks;
          } else {
            console.warn('[fetchTasks] ⚠️ 新任務未找到，taskId:', newTaskId, 'sortedTasks:', sortedTasks.map(t => t.id));
          }
        } else {
          console.log('[fetchTasks] ⚠️ addingTaskRef.current?.taskId 不存在，addingTaskRef.current:', addingTaskRef.current);
        }
        
        // 如果之前正在編輯任務，且該任務仍然存在，保持編輯狀態
        if (preserveEditingState && currentEditingTaskId && currentEditingTask) {
          // 如果要求保持編輯狀態（例如在添加任務後），直接恢復之前的編輯狀態
          const stillExists = sortedTasks.find(t => Number(t.id) === Number(currentEditingTaskId));
          if (stillExists) {
            console.log('[fetchTasks] 保持編輯狀態，任務ID:', stillExists.id);
            // 恢復編輯狀態，但更新任務的基本信息（保留用戶正在編輯的內容）
            setEditingTask({
              ...currentEditingTask,
              id: Number(stillExists.id), // 確保 id 是數字
              // 保留用戶正在編輯的內容（title, content）
              // 只更新可能變化的字段
              task_type: currentEditingTask.task_type || stillExists.task_type,
              requires_submission: currentEditingTask.requires_submission ?? stillExists.requires_submission,
              enable_question_wall: currentEditingTask.enable_question_wall ?? (stillExists.enable_question_wall === true || stillExists.enable_question_wall === 'true' || stillExists.enable_question_wall === 1)
            });
          } else {
            console.log('[fetchTasks] 任務不存在，但 preserveEditingState=true，不清除編輯狀態');
          }
        } else if (currentEditingTaskId && !preserveEditingState) {
          // 如果正在編輯任務，即使 preserveEditingState 為 false，也要保持編輯狀態
          // 因為編輯狀態應該只在保存、取消或關閉對話框時清除
          const stillExists = sortedTasks.find(t => Number(t.id) === Number(currentEditingTaskId));
          if (stillExists && editingTask) {
            // 更新編輯任務的數據，但保留用戶的編輯內容（title, content）
            console.log('[fetchTasks] 保持編輯狀態，更新任務數據但保留用戶編輯內容');
            setEditingTask({
              ...editingTask,
              id: Number(stillExists.id), // 確保 id 是數字
              // 保留用戶正在編輯的內容（title, content）
              // 只更新可能變化的字段
              task_type: editingTask.task_type || stillExists.task_type,
              requires_submission: editingTask.requires_submission ?? stillExists.requires_submission,
              enable_question_wall: editingTask.enable_question_wall ?? (stillExists.enable_question_wall === true || stillExists.enable_question_wall === 'true' || stillExists.enable_question_wall === 1)
            });
          } else if (!stillExists) {
            // 如果任務不存在了，清除編輯狀態
            // 但如果正在添加任務，不要清除
            if (!addingTaskRef.current?.taskId) {
              console.log('[fetchTasks] 任務不存在，清除編輯狀態');
              setEditingTask(null);
            } else {
              console.log('[fetchTasks] 任務不存在，但正在添加任務，不清除編輯狀態');
            }
          }
        }
        
        return sortedTasks; // 返回任務列表
      }
      return []; // 如果沒有成功，返回空陣列
    } catch (error) {
      console.error('獲取任務資料錯誤:', error);
      // 如果 API 失敗，使用原本的資料（按照 unit_number 和 subtask_number 排序）
      const fetchedTasks = packageData.LessonPackageTasks || [];
      const sortedTasks = [...fetchedTasks].sort((a, b) => {
        const aUnit = a.unit_number ?? 1;
        const bUnit = b.unit_number ?? 1;
        if (aUnit !== bUnit) {
          return aUnit - bUnit;
        }
        const aSubtask = typeof a.subtask_number === 'number' ? a.subtask_number : (parseInt(a.subtask_number) || 0);
        const bSubtask = typeof b.subtask_number === 'number' ? b.subtask_number : (parseInt(b.subtask_number) || 0);
        return aSubtask - bSubtask;
      });
      setTasks(sortedTasks);
      return sortedTasks;
    } finally {
      // 如果正在添加任務，不要設置 loading，避免觸發父組件重新渲染導致 Dialog 被卸載
      if (!addingTaskRef.current) {
        setLoading(false);
      }
    }
  };

  const getTaskStatusIcon = (task) => {
    // 根據任務類型顯示不同圖標，處理 undefined 情況
    const taskType = task?.task_type || 'idea'; // 預設為 'idea'
    switch (taskType) {
      case 'experiment': return <AssignmentIcon sx={{ color: '#4caf50' }} />;
      case 'question': return <ScheduleIcon sx={{ color: '#ff9800' }} />;
      case 'idea': return <CheckCircleIcon sx={{ color: '#2196f3' }} />;
      case 'record': return <AssignmentIcon sx={{ color: '#9c27b0' }} />;
      case 'info': return <AssignmentIcon sx={{ color: '#607d8b' }} />;
      default: return <AssignmentIcon sx={{ color: '#9e9e9e' }} />;
    }
  };

  const getTaskStatusColor = (task) => {
    // 根據任務類型顯示不同顏色，處理 undefined 情況
    const taskType = task?.task_type || 'idea'; // 預設為 'idea'
    switch (taskType) {
      case 'experiment': return 'success';
      case 'question': return 'warning';
      case 'idea': return 'primary';
      case 'record': return 'secondary';
      case 'info': return 'default';
      default: return 'default';
    }
  };

  // 用於追蹤是否應該在首次聚焦時選中文字
  const shouldSelectOnFocusRef = React.useRef(false);

  const handleEditTask = (task) => {
    console.log('[前端] 開始編輯任務:', {
      taskId: task.id,
      taskIdType: typeof task.id,
      enable_question_wall: task.enable_question_wall,
      type: typeof task.enable_question_wall
    });
    // 確保 id 是數字，enable_question_wall 是布林值
    const taskCopy = {
      ...task,
      id: Number(task.id), // 正規化 id 為數字
      enable_question_wall: task.enable_question_wall === true || task.enable_question_wall === 'true' || task.enable_question_wall === 1
    };
    // 設置編輯任務時，標記下次聚焦時應該選中文字（僅首次）
    shouldSelectOnFocusRef.current = true;
    setEditingTask(taskCopy);
  };

  const handleSaveTask = async () => {
    if (editingTask) {
      try {
        setLoading(true);
        
        // 準備要傳送的資料（不更新 unit_number 和 subtask_number，因為是自動生成的）
        const updateData = {
          title: editingTask.title,
          content: editingTask.content,
          task_type: editingTask.task_type,
          requires_submission: editingTask.requires_submission,
          enable_question_wall: editingTask.enable_question_wall === true || editingTask.enable_question_wall === 'true'
          // 不包含 unit_number 和 subtask_number，讓後端保持原有值
        };
        
        const response = await axiosInstance.put(
          `${config.updateLessonPackageTask}${packageData.id}/tasks/${editingTask.id}`,
          updateData
        );

        if (response.data.success) {
          // 重新載入任務列表以確保資料同步（不保持編輯狀態，因為已保存）
          await fetchTasks(false);
          setEditingTask(null);
          onSuccess('任務已更新');
        } else {
          alert('更新任務失敗');
        }
      } catch (error) {
        console.error('更新任務錯誤:', error);
        alert('更新任務失敗');
      } finally {
        setLoading(false);
      }
    }
  };

  /**
   * 新增任務
   * @param {string} type - 'unit' 新增章節 或 'subtask' 新增子任務
   * @param {number|null} targetUnitNumber - 指定章節號（新增子任務時使用）
   */
  const handleAddTask = async (type = 'subtask', targetUnitNumber = null) => {
    // 防重複點擊：如果正在處理中，直接返回
    if (loading || addingTaskRef.current) {
      console.log('[handleAddTask] 正在處理中，忽略重複點擊');
      return;
    }

    try {
      setLoading(true);
      // 標記正在添加任務
      addingTaskRef.current = { type, targetUnitNumber, timestamp: Date.now() };
      
      // 使用 groupTasksByUnit() 來計算，確保與顯示邏輯一致
      const { grouped } = groupTasksByUnit();
      const unitKeys = Object.keys(grouped).sort((a, b) => {
        return grouped[a].unitNumber - grouped[b].unitNumber;
      });
      
      // 計算新的章節/子任務編號
      let unitNumber = null;
      let subtaskNumber = null;
      
      if (type === 'unit') {
        // 新增章節：找到最大章節號 + 1
        if (unitKeys.length > 0) {
          const lastUnitKey = unitKeys[unitKeys.length - 1];
          const lastUnit = grouped[lastUnitKey];
          unitNumber = lastUnit.unitNumber + 1;
        } else {
          unitNumber = 1; // 如果沒有任務，從章節1開始
        }
        subtaskNumber = '1'; // 新章節的第一個子任務
      } else {
        // 新增子任務：如果指定了章節號，使用該章節；否則使用最後一個章節
        if (targetUnitNumber !== null) {
          unitNumber = targetUnitNumber;
          const targetUnitKey = `unit_${targetUnitNumber}`;
          const targetUnit = grouped[targetUnitKey];
          
          if (targetUnit && targetUnit.tasks.length > 0) {
            // 找到該章節的最大子任務號
            const maxSubtask = Math.max(
              ...targetUnit.tasks.map(t => {
                const stNum = t.subtask_number || t.displaySubtaskNumber || '0';
                const num = parseInt(stNum);
                return isNaN(num) ? 0 : num;
              }),
              0
            );
            subtaskNumber = maxSubtask + 1;
          } else {
            subtaskNumber = 1;
          }
        } else if (unitKeys.length > 0) {
          // 使用最後一個章節
          const lastUnitKey = unitKeys[unitKeys.length - 1];
          const lastUnit = grouped[lastUnitKey];
          unitNumber = lastUnit.unitNumber;
          
          // 找到該章節的最大子任務號
          const maxSubtask = Math.max(
            ...lastUnit.tasks.map(t => {
              const stNum = t.subtask_number || t.displaySubtaskNumber || 0;
              return typeof stNum === 'number' ? stNum : (parseInt(stNum) || 0);
            }),
            0
          );
          subtaskNumber = maxSubtask + 1;
        } else {
          // 如果沒有任務，從章節1開始
          unitNumber = 1;
          subtaskNumber = 1;
        }
      }
      
      // 根據 unit_number 和 subtask_number 計算 order_index
      const orderIndex = calculateOrderIndex(unitNumber, subtaskNumber);
      
      const taskData = {
        lesson_package_id: packageData.id,
        title: '',
        content: '',
        task_type: 'idea',
        order_index: orderIndex,
        requires_submission: false,
        enable_question_wall: false,
        unit_number: unitNumber,
        subtask_number: subtaskNumber
      };

      const response = await axiosInstance.post(
        `${config.createLessonPackageTask}${packageData.id}/tasks`,
        taskData
      );

      if (response.data.success) {
        console.log('[handleAddTask] 任務建立成功，開始查找新任務');
        console.log('[handleAddTask] API 返回的任務:', response.data.task);
        console.log('[handleAddTask] 預期的 unit_number:', unitNumber, 'subtask_number:', subtaskNumber);
        
        // 先保存新任務的 ID（如果 API 返回了）
        const newTaskId = response.data.task?.id;
        console.log('[handleAddTask] 新任務 ID:', newTaskId);
        
        // 立即更新添加任務標記（在調用任何可能觸發 fetchTasks 的函數之前）
        addingTaskRef.current = { unitNumber, subtaskNumber, taskId: newTaskId, timestamp: Date.now() };
        console.log('[handleAddTask] ✅ addingTaskRef.current 已設置:', addingTaskRef.current);
        
        // 確保新任務在視圖中可見（先展開對應的章節）
        if (unitNumber) {
          const unitKey = `unit_${unitNumber}`;
          console.log('[handleAddTask] 展開章節:', unitKey);
          setExpandedUnits(prev => {
            const newState = {
              ...prev,
              [unitKey]: true
            };
            console.log('[handleAddTask] 更新後的展開狀態:', newState);
            return newState;
          });
        }
        
        // 先使用 API 返回的任務，立即設置編輯狀態（不需要等待 fetchTasks）
        let newTask = response.data.task;
        if (!newTask && newTaskId) {
          // 如果 API 沒有返回任務，嘗試從現有任務列表中找到
          newTask = tasks.find(t => Number(t.id) === Number(newTaskId));
        }
        
        // 如果還是找不到，重新獲取任務列表
        if (!newTask) {
          console.log('[handleAddTask] API 未返回任務，重新獲取任務列表');
          // 注意：此時還沒有設置編輯任務，但我們已經設置了 addingTaskRef.current.taskId
          // 所以 fetchTasks 會檢測到正在添加任務，不會清除編輯狀態
          console.log('[handleAddTask] 準備調用 fetchTasks，addingTaskRef.current:', addingTaskRef.current);
          const updatedTasks = await fetchTasks(false);
          console.log('[handleAddTask] 重新獲取的任務列表:', updatedTasks);
          
          // 從更新後的任務列表中找到新建立的任務
          if (newTaskId) {
            newTask = updatedTasks?.find(t => Number(t.id) === Number(newTaskId));
            console.log('[handleAddTask] 使用 ID 匹配結果:', newTask);
          }
          
          if (!newTask) {
            // 如果找不到，嘗試根據 unit_number 和 subtask_number 匹配
            console.log('[handleAddTask] ID 匹配失敗，嘗試使用 unit_number 和 subtask_number 匹配');
            newTask = updatedTasks?.find(t => {
              const tUnit = t.unit_number ?? 1;
              const tSubtask = typeof t.subtask_number === 'number' ? t.subtask_number : (parseInt(t.subtask_number) || 0);
              const match = tUnit === unitNumber && tSubtask === subtaskNumber;
              console.log(`[handleAddTask] 檢查任務 ${t.id}: unit=${tUnit}, subtask=${tSubtask}, 匹配=${match}`);
              return match;
            });
            console.log('[handleAddTask] unit_number/subtask_number 匹配結果:', newTask);
          }
        }
        
        console.log('[handleAddTask] 最終找到的新任務:', newTask);
        
        if (newTask) {
          console.log('[handleAddTask] 設置編輯任務');
          
          // 確保在設置編輯任務之前，addingTaskRef 仍然存在
          if (!addingTaskRef.current?.taskId) {
            console.warn('[handleAddTask] ⚠️ addingTaskRef.current.taskId 不存在，重新設置');
            addingTaskRef.current = { taskId: Number(newTask.id), timestamp: Date.now() };
          }
          
          // 步驟 1: 先確保章節展開
          if (newTask.unit_number) {
            const unitKey = `unit_${newTask.unit_number}`;
            console.log('[handleAddTask] 確保章節展開:', unitKey);
            setExpandedUnits(prev => {
              const newState = {
                ...prev,
                [unitKey]: true
              };
              console.log('[handleAddTask] 展開狀態已更新:', newState);
              return newState;
            });
          }
          
          // 步驟 2: 立即設置編輯任務（不等待章節展開，因為章節展開是同步的）
          const editingTaskData = {
            ...newTask,
            id: Number(newTask.id), // 正規化 id 為數字
            enable_question_wall: newTask.enable_question_wall === true || newTask.enable_question_wall === 'true' || newTask.enable_question_wall === 1
          };
          console.log('[handleAddTask] 準備設置編輯任務:', editingTaskData);
          
          // 立即設置編輯任務
          // 確保 addingTaskRef 在設置編輯任務時存在，防止 fetchTasks 清除編輯狀態
          if (!addingTaskRef.current?.taskId) {
            console.warn('[handleAddTask] ⚠️ addingTaskRef.current.taskId 不存在，重新設置');
            addingTaskRef.current = { taskId: Number(editingTaskData.id), timestamp: Date.now() };
          }
          
          console.log('[handleAddTask] 設置編輯任務前，addingTaskRef:', addingTaskRef.current);
          
          // 確保 addingTaskRef 在設置編輯任務後仍然存在，防止後續的 fetchTasks 清除編輯狀態
          addingTaskRef.current = { taskId: Number(editingTaskData.id), timestamp: Date.now() };
          console.log('[handleAddTask] 設置編輯任務前，確保 addingTaskRef 存在:', addingTaskRef.current);
          
          // 新增任務時，標題為空，不需要選中文字
          shouldSelectOnFocusRef.current = false;
          setEditingTask(editingTaskData);
          console.log('[handleAddTask] ✅ 編輯任務已設置，任務ID:', editingTaskData.id, '型別:', typeof editingTaskData.id);
          
          // 步驟 3: 確保任務列表已更新，然後滾動
          // 先調用 fetchTasks 更新任務列表（但不會清除編輯狀態，因為 addingTaskRef 已設置）
          await fetchTasks(false);
          console.log('[handleAddTask] fetchTasks 完成，開始滾動');
          
          // 步驟 4: 等待 DOM 更新和 Accordion 動畫完成後，再滾動
          const newId = Number(newTask.id);
          // 使用多重 requestAnimationFrame 和短延遲確保 DOM 完全渲染
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setTimeout(() => {
                const taskElement = taskItemRefs.current[newId];
                if (taskElement) {
                  console.log('[handleAddTask] ✅ 找到任務元素 ref，開始滾動');
                  taskElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else {
                  console.warn('[handleAddTask] ⚠️ 任務元素 ref 尚未綁定，任務ID:', newId, '可用的 refs:', Object.keys(taskItemRefs.current));
                }
              }, 100); // 額外等待 100ms 確保 Accordion 動畫完成
            });
          });
          
          // 步驟 5: 在 fetchTasks 完成後才調用 onSuccess，避免觸發父組件重新渲染導致 Dialog 被卸載
          // 使用 setTimeout 確保所有狀態更新完成後再調用
          setTimeout(() => {
            onSuccess(`任務已建立（${type === 'unit' ? '新章節' : '新子任務'}），請編輯任務內容`);
          }, 0);
        } else {
          console.error('[handleAddTask] 無法找到新建立的任務');
        }
      } else {
        alert('建立任務失敗');
        setLoading(false);
        addingTaskRef.current = null; // 清除標記
      }
    } catch (error) {
      console.error('建立任務錯誤:', error);
      alert('建立任務失敗');
      setLoading(false);
      addingTaskRef.current = null; // 清除標記
    } finally {
      setLoading(false);
      // 延遲清除標記，避免快速重複點擊（但保留足夠時間讓編輯模式設置完成）
      setTimeout(() => {
        addingTaskRef.current = null;
      }, 1500); // 1.5秒後清除標記
    }
  };

  /**
   * 刪除任務
   * 後端會自動重新編號：
   * - 如果該章節還有其他任務：只重新編號該章節內的子任務
   * - 如果該章節沒有其他任務了：把後面所有章節往前移
   */
  const handleDeleteTask = async (taskId) => {
    if (window.confirm('確定要刪除這個任務嗎？')) {
      try {
        setLoading(true);
        const response = await axiosInstance.delete(
          `${config.deleteLessonPackageTask}${packageData.id}/tasks/${taskId}`
        );

        if (response.data.success) {
          // 重新獲取任務列表，確保資料同步（後端會自動重新編號）
          await fetchTasks();
      onSuccess('任務已刪除');
        } else {
          alert('刪除任務失敗');
        }
      } catch (error) {
        console.error('刪除任務錯誤:', error);
        alert('刪除任務失敗');
      } finally {
        setLoading(false);
      }
    }
  };


  /**
   * 計算 order_index 的輔助函數
   * order_index 由 unit_number 和 subtask_number 推導出來
   * 公式：order_index = (unit_number - 1) * 10000 + subtask_number
   * 使用 10000 倍數以支持每個章節最多 9999 個子任務
   */
  const calculateOrderIndex = (unitNumber, subtaskNumber) => {
    const unit = unitNumber || 1;
    // 將 subtaskNumber 轉換為數字
    let subtask = subtaskNumber;
    if (typeof subtask === 'string') {
      subtask = parseInt(subtask, 10);
    }
    if (typeof subtask !== 'number' || isNaN(subtask) || subtask < 1) {
      subtask = 1;
    }
    return (unit - 1) * 10000 + subtask;
  };

  /**
   * 調整任務順序
   * 只交換兩個任務的 unit_number 和 subtask_number，不會改到其他章節的任務
   * 然後根據 unit_number 和 subtask_number 推導出 order_index
   * @param {number} taskId - 要移動的任務ID
   * @param {string} direction - 'up' 向上移動 或 'down' 向下移動
   */
  const handleMoveTask = async (taskId, direction) => {
    try {
      setLoading(true);
      
      // 按照 unit_number 和 subtask_number 排序任務列表（用於確定移動位置）
      const sortedTasks = [...tasks].sort((a, b) => {
        const aUnit = a.unit_number ?? 1;
        const bUnit = b.unit_number ?? 1;
        if (aUnit !== bUnit) {
          return aUnit - bUnit;
        }
        const aSubtask = typeof a.subtask_number === 'number' ? a.subtask_number : (parseInt(a.subtask_number) || 0);
        const bSubtask = typeof b.subtask_number === 'number' ? b.subtask_number : (parseInt(b.subtask_number) || 0);
        return aSubtask - bSubtask;
      });
      
      const taskIndex = sortedTasks.findIndex(task => task.id === taskId);
      
      if (taskIndex === -1) {
        alert('找不到要移動的任務');
        return;
      }

      const newIndex = direction === 'up' ? taskIndex - 1 : taskIndex + 1;
      
      if (newIndex < 0 || newIndex >= sortedTasks.length) {
        return; // 已經到達邊界
      }

      // 只交換兩個任務的 unit_number 和 subtask_number
      const task1 = sortedTasks[taskIndex];
      const task2 = sortedTasks[newIndex];
      
      // 交換章節編號
      const tempUnit = task1.unit_number;
      const tempSubtask = task1.subtask_number;
      
      const updatedTask1 = {
        ...task1,
        unit_number: task2.unit_number,
        subtask_number: task2.subtask_number,
        order_index: calculateOrderIndex(task2.unit_number, task2.subtask_number)
      };
      
      const updatedTask2 = {
        ...task2,
        unit_number: tempUnit,
        subtask_number: tempSubtask,
        order_index: calculateOrderIndex(tempUnit, tempSubtask)
      };

      // 更新這兩個任務
      const updatePromises = [
        axiosInstance.put(
          `${config.updateLessonPackageTask}${packageData.id}/tasks/${updatedTask1.id}`,
          {
            title: updatedTask1.title,
            content: updatedTask1.content,
            task_type: updatedTask1.task_type,
            requires_submission: updatedTask1.requires_submission,
            enable_question_wall: updatedTask1.enable_question_wall,
            order_index: updatedTask1.order_index,
            unit_number: updatedTask1.unit_number,
            subtask_number: updatedTask1.subtask_number
          }
        ),
        axiosInstance.put(
          `${config.updateLessonPackageTask}${packageData.id}/tasks/${updatedTask2.id}`,
        {
            title: updatedTask2.title,
            content: updatedTask2.content,
            task_type: updatedTask2.task_type,
            requires_submission: updatedTask2.requires_submission,
            enable_question_wall: updatedTask2.enable_question_wall,
            order_index: updatedTask2.order_index,
            unit_number: updatedTask2.unit_number,
            subtask_number: updatedTask2.subtask_number
        }
        )
      ];
      
      await Promise.all(updatePromises);
      
      // 重新獲取任務列表以確保資料同步
      await fetchTasks();
        onSuccess('任務順序已調整');
    } catch (error) {
      console.error('調整任務順序錯誤:', error);
      alert(`調整任務順序失敗: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 自動生成章節/子任務編號的輔助函數
  const generateUnitSubtaskNumber = (task, index, allTasks) => {
    // 如果已經有設定，直接使用
    if (task.unit_number && task.subtask_number) {
      return { unitNumber: task.unit_number, subtaskNumber: task.subtask_number };
    }
    
    // 如果都沒有設定，自動生成：1-1, 1-2, 1-3...
    if (!task.unit_number && !task.subtask_number) {
      return { unitNumber: 1, subtaskNumber: index + 1 };
    }
    
    // 如果只有 unit_number，在同章節內自動生成子任務編號
    if (task.unit_number && !task.subtask_number) {
      const sameUnitTasks = allTasks.filter(t => t.unit_number === task.unit_number);
      const sameUnitIndex = sameUnitTasks.findIndex(t => t.id === task.id);
      return { unitNumber: task.unit_number, subtaskNumber: sameUnitIndex + 1 };
    }
    
    // 如果只有 subtask_number，預設為章節1
    if (!task.unit_number && task.subtask_number) {
      const subtask = typeof task.subtask_number === 'number' ? task.subtask_number : parseInt(task.subtask_number) || 1;
      return { unitNumber: 1, subtaskNumber: subtask };
    }
    
    const subtask = typeof task.subtask_number === 'number' ? task.subtask_number : (parseInt(task.subtask_number) || (index + 1));
    return { unitNumber: task.unit_number || 1, subtaskNumber: subtask };
  };

  // 按章節分組任務（自動生成未設定的編號）
  const groupTasksByUnit = () => {
    const grouped = {};
    
    // 先為所有任務生成編號
    const tasksWithNumbers = tasks.map((task, index) => {
      const { unitNumber, subtaskNumber } = generateUnitSubtaskNumber(task, index, tasks);
      return {
        ...task,
        displayUnitNumber: unitNumber,
        displaySubtaskNumber: subtaskNumber
      };
    });
    
    // 按章節分組
    tasksWithNumbers.forEach(task => {
      const unitKey = `unit_${task.displayUnitNumber}`;
      
      if (!grouped[unitKey]) {
        grouped[unitKey] = {
          unitNumber: task.displayUnitNumber,
          tasks: []
        };
      }
      grouped[unitKey].tasks.push(task);
    });
    
    // 對每個章節的任務按 subtask_number 排序
    Object.keys(grouped).forEach(key => {
      grouped[key].tasks.sort((a, b) => {
        // 按子任務編號排序（整數）
        const aNum = typeof a.displaySubtaskNumber === 'number' ? a.displaySubtaskNumber : (parseInt(a.displaySubtaskNumber) || 0);
        const bNum = typeof b.displaySubtaskNumber === 'number' ? b.displaySubtaskNumber : (parseInt(b.displaySubtaskNumber) || 0);
        return aNum - bNum;
      });
    });
    
    return { grouped, ungrouped: [] };
  };

  const { grouped } = groupTasksByUnit();
  const unitKeys = Object.keys(grouped).sort((a, b) => {
    return grouped[a].unitNumber - grouped[b].unitNumber;
  });

  const toggleUnit = (unitKey) => {
    setExpandedUnits(prev => ({
      ...prev,
      [unitKey]: !prev[unitKey]
    }));
  };

  if (!packageData) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '70vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #2b687f 0%, #1e4d5f 100%)',
        color: 'white',
        pb: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ 
            bgcolor: 'rgba(255,255,255,0.2)', 
            borderRadius: 2, 
            p: 1, 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <AssignmentIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {packageData.name}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              任務管理與編輯
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3, bgcolor: '#f8f9fa' }}>
        <Box sx={{ 
          mb: 3, 
          p: 2.5, 
          bgcolor: 'white', 
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 2 }}>
            {packageData.description || '暫無描述'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {packageData.subject && (
              <Chip 
                label={packageData.subject} 
                size="small" 
                sx={{ bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 500 }}
              />
            )}
            {packageData.grade && (
              <Chip 
                label={packageData.grade} 
                size="small" 
                sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 500 }}
              />
            )}
            <Chip 
              label={packageData.status === 'published' ? '已發布' : '草稿'} 
              size="small" 
              color={packageData.status === 'published' ? 'success' : 'default'}
              sx={{ fontWeight: 500 }}
            />
          </Box>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2.5,
          p: 2,
          bgcolor: 'white',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2b687f' }}>
            任務列表 
            <Chip 
              label={tasks.length} 
              size="small" 
              color="primary"
              sx={{ ml: 1.5 }}
            />
          </Typography>
          <Button
            variant="contained"
            size="medium"
            className="btn-primary"
            onClick={() => handleAddTask('unit')}
            sx={{ 
              borderRadius: 2,
              px: 2.5,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(43,104,127,0.3)'
            }}
          >
            <AddIcon sx={{ mr: 0.5 }} />
            新增章節
          </Button>
        </Box>

          {tasks.length > 0 ? (
          <List>
            {/* 按章節分組顯示 */}
            {unitKeys.length > 0 && unitKeys.map(unitKey => {
              const unitData = grouped[unitKey];
              const isExpanded = expandedUnits[unitKey] !== false; // 預設展開
              
              return (
                <Accordion
                  key={unitKey}
                  expanded={isExpanded}
                  onChange={() => toggleUnit(unitKey)}
                  sx={{
                  mb: 1.5,
                    '&:before': { display: 'none' },
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  borderRadius: 2,
                    overflow: 'hidden'
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: '#e3f2fd',
                      minHeight: 48,
                      '&.Mui-expanded': {
                        minHeight: 48
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        章節 {unitData.unitNumber} ({unitData.tasks.length} 個任務)
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddTask('subtask', unitData.unitNumber);
                        }}
                        sx={{
                          color: '#1976d2',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.1)'
                          }
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0, bgcolor: '#fafafa' }}>
                    {unitData.tasks.map((task, index) => {
                      // 計算該任務在所有任務中的索引（用於判斷是否可以移動）
                      // 按照 unit_number 和 subtask_number 排序，與顯示順序一致
                      const sortedAllTasks = [...tasks].sort((a, b) => {
                        const aUnit = a.unit_number ?? 1;
                        const bUnit = b.unit_number ?? 1;
                        if (aUnit !== bUnit) {
                          return aUnit - bUnit;
                        }
                        const aSubtask = typeof a.subtask_number === 'number' ? a.subtask_number : (parseInt(a.subtask_number) || 0);
                        const bSubtask = typeof b.subtask_number === 'number' ? b.subtask_number : (parseInt(b.subtask_number) || 0);
                        return aSubtask - bSubtask;
                      });
                      const globalTaskIndex = sortedAllTasks.findIndex(t => t.id === task.id);
                      const canMoveUp = globalTaskIndex > 0;
                      const canMoveDown = globalTaskIndex < sortedAllTasks.length - 1;
                      
                      
                      return (
                      <ListItem
                        key={task.id}
                        data-task-id={task.id}
                        ref={(el) => {
                          const id = Number(task.id);
                          if (el) {
                            taskItemRefs.current[id] = el;
                          } else {
                            delete taskItemRefs.current[id];
                          }
                        }}
                        sx={{
                          mb: 1,
                          p: 2,
                          borderRadius: 1,
                  border: '1px solid',
                  borderColor: Number(editingTask?.id) === Number(task.id) ? '#2b687f' : '#e0e0e0',
                          backgroundColor: Number(editingTask?.id) === Number(task.id) ? '#f0f8ff' : 'white',
                  transition: 'all 0.3s',
                          ml: 1,
                          mr: 1,
                          mt: index === 0 ? 1 : 0,
                          mb: index === unitData.tasks.length - 1 ? 1 : 1,
                  '&:hover': {
                    borderColor: '#2b687f',
                    boxShadow: '0 4px 12px rgba(43,104,127,0.15)',
                    transform: 'translateY(-2px)'
                  }
                        }}
                      >
                  <ListItemIcon>
                    {getTaskStatusIcon(task)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      Number(editingTask?.id) === Number(task.id) ? (
                        <Box component="div">
                          <TextField
                            fullWidth
                            autoFocus
                            value={editingTask.title}
                            onChange={(e) => {
                              const value = e.target.value || '';
                              if (value.length <= 15) {
                                setEditingTask({ ...editingTask, title: value });
                                // 一旦用戶開始輸入，就不再自動選中
                                shouldSelectOnFocusRef.current = false;
                              }
                            }}
                            onFocus={(e) => {
                              // 只在首次進入編輯模式時選中文字
                              if (shouldSelectOnFocusRef.current && e.target.value) {
                                e.target.select();
                                shouldSelectOnFocusRef.current = false; // 選中後立即清除標記
                              }
                            }}
                            size="small"
                            placeholder="請輸入任務標題"
                            helperText={`${Math.min((editingTask.title || '').length, 15)}/15 字`}
                            inputProps={{ maxLength: 15 }}
                          />
                        </Box>
                      ) : (
                      <Box component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`${task.displayUnitNumber || task.unit_number || 1}-${task.displaySubtaskNumber || task.subtask_number || 1}`}
                          size="small"
                          sx={{
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            fontSize: '0.7rem',
                            height: 20,
                            fontWeight: 500
                          }}
                        />
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {task.title || '未命名任務'}
                        </Typography>
                        {/* 顯示成果和問題牆標籤 */}
                        {task.requires_submission && (
                          <Chip
                            label="需繳交成果"
                            size="small"
                            sx={{
                              backgroundColor: '#e8f5e9',
                              color: '#2e7d32',
                              fontSize: '0.65rem',
                              height: 18,
                              fontWeight: 500,
                              ml: 0.5
                            }}
                          />
                        )}
                        {(task.enable_question_wall === true || task.enable_question_wall === 'true' || task.enable_question_wall === 1) && (
                          <Chip
                            label="使用問題牆"
                            size="small"
                            sx={{
                              backgroundColor: '#e3f2fd',
                              color: '#1976d2',
                              fontSize: '0.65rem',
                              height: 18,
                              fontWeight: 500,
                              ml: 0.5
                            }}
                          />
                        )}
                      </Box>
                      )
                    }
                    secondary={
                      Number(editingTask?.id) === Number(task.id) ? (
                        <Box component="div" sx={{ mt: 1 }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                            value={editingTask.content}
                            onChange={(e) => setEditingTask({ ...editingTask, content: e.target.value })}
                          size="small"
                            sx={{ mb: 2 }}
                            placeholder="請輸入任務描述"
                          />
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>任務類型</InputLabel>
                              <Select
                                value={editingTask.task_type || 'idea'}
                                onChange={(e) => setEditingTask({ ...editingTask, task_type: e.target.value })}
                                label="任務類型"
                              >
                                <MenuItem value="idea">想法</MenuItem>
                                <MenuItem value="question">提問</MenuItem>
                                <MenuItem value="info">資訊</MenuItem>
                                <MenuItem value="experiment">實驗</MenuItem>
                                <MenuItem value="record">紀錄</MenuItem>
                              </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>繳交要求</InputLabel>
                              <Select
                                value={editingTask.requires_submission ? 'true' : 'false'}
                                onChange={(e) => setEditingTask({ ...editingTask, requires_submission: e.target.value === 'true' })}
                                label="繳交要求"
                              >
                                <MenuItem value="false">不需要</MenuItem>
                                <MenuItem value="true">需要</MenuItem>
                              </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                              <InputLabel id="question-wall-label">使用問題牆引導提問</InputLabel>
                              <Select
                                labelId="question-wall-label"
                                value={editingTask.enable_question_wall === true || editingTask.enable_question_wall === 'true' ? 'true' : 'false'}
                                onChange={(e) => {
                                  const newValue = e.target.value === 'true';
                                  setEditingTask({ ...editingTask, enable_question_wall: newValue });
                                }}
                                label="使用問題牆引導提問"
                              >
                                <MenuItem value="false">不啟用</MenuItem>
                                <MenuItem value="true">啟用</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                        </Box>
                      ) : (
                        task.content || '請點擊編輯按鈕添加任務描述'
                      )
                    }
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {Number(editingTask?.id) === Number(task.id) ? (
                      <>
                        <Button
                          size="small"
                          variant="contained"
                          className="btn-primary"
                          onClick={handleSaveTask}
                        >
                          保存
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            // 清除 draft（取消編輯）
                            if (draftKey) {
                              sessionStorage.removeItem(draftKey);
                            }
                            setEditingTask(null);
                          }}
                        >
                          取消
                        </Button>
                      </>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            sx={{ color: '#666' }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleMoveTask(task.id, 'up');
                          }}
                          disabled={!canMoveUp || loading}
                          >
                            <ArrowUpIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            sx={{ color: '#666' }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleMoveTask(task.id, 'down');
                          }}
                          disabled={!canMoveDown || loading}
                          >
                            <ArrowDownIcon />
                          </IconButton>
                        </Box>
                        <Chip
                          label={task.task_type || 'idea'}
                          color={getTaskStatusColor(task)}
                          size="small"
                        />
                        <IconButton
                          size="small"
                          sx={{ color: '#2b687f' }}
                          onClick={() => handleEditTask(task)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{ color: '#f44336' }}
                          onClick={() => handleDeleteTask(task.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </ListItem>
                    );
                    })}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </List>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 8,
              bgcolor: '#fafafa',
              borderRadius: 2,
              border: '2px dashed #e0e0e0'
            }}>
              <AssignmentIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                目前沒有任務
              </Typography>
              <Typography variant="body2" color="text.secondary">
                點擊上方「新增任務」按鈕來建立第一個任務
              </Typography>
            </Box>
          )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ 
            borderRadius: 2,
            px: 3,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          關閉
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// 建立備課包對話框組件
function CreateLessonPackageDialog({ open, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    subject: '',
    grade: '',
    status: 'preparing'
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field) => (event) => {
    const newValue = event.target.value;
    console.log(`Field ${field} changed to:`, newValue);
    setFormData({
      ...formData,
      [field]: newValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('=== FRONTEND FORM SUBMISSION DEBUG ===');
    console.log('Form data before validation:', formData);
    console.log('Form data name field:', formData.name);
    console.log('Form data name type:', typeof formData.name);
    console.log('Form data name trimmed:', formData.name?.trim());
    
    if (!formData.name || !formData.name.trim()) {
      alert('請輸入備課包名稱');
      return;
    }

    try {
      setLoading(true);
      console.log('Sending data to backend:', formData);
      const response = await axiosInstance.post(config.createLessonPackage, formData);
      
      if (response.data.success) {
        onSuccess('備課包建立成功');
        setFormData({
          name: '',
          description: '',
          subject: '',
          grade: '',
          status: 'preparing'
        });
        onClose();
      } else {
        alert('建立失敗');
      }
    } catch (error) {
      console.error('建立備課包錯誤:', error);
      alert('建立失敗');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BookIcon sx={{ color: '#2b687f' }} />
          <Typography variant="h6">建立備課包</Typography>
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              label="備課包名稱"
              value={formData.name}
              onChange={handleInputChange('name')}
              required
              fullWidth
              placeholder="請輸入備課包名稱"
            />

            <TextField
              label="備課包描述"
              value={formData.description}
              onChange={handleInputChange('description')}
              multiline
              rows={3}
              fullWidth
              placeholder="請輸入備課包描述"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>學科</InputLabel>
                <Select
                  value={formData.subject}
                  onChange={handleInputChange('subject')}
                  label="學科"
                >
                  <MenuItem value="自然">自然</MenuItem>
                  <MenuItem value="數學">數學</MenuItem>
                  <MenuItem value="國語">國語</MenuItem>
                  <MenuItem value="社會">社會</MenuItem>
                  <MenuItem value="英語">英語</MenuItem>
                  <MenuItem value="其他">其他</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ flex: 1 }}>
                <InputLabel>年級</InputLabel>
                <Select
                  value={formData.grade}
                  onChange={handleInputChange('grade')}
                  label="年級"
                >
                  <MenuItem value="一年級">一年級</MenuItem>
                  <MenuItem value="二年級">二年級</MenuItem>
                  <MenuItem value="三年級">三年級</MenuItem>
                  <MenuItem value="四年級">四年級</MenuItem>
                  <MenuItem value="五年級">五年級</MenuItem>
                  <MenuItem value="六年級">六年級</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <FormControl>
              <InputLabel>狀態</InputLabel>
              <Select
                value={formData.status}
                onChange={handleInputChange('status')}
                label="狀態"
              >
                <MenuItem value="preparing">備課中</MenuItem>
                <MenuItem value="completed">完成</MenuItem>
              
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
          <Button type="button" onClick={onClose}>
            取消
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? '建立中...' : '建立備課包'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// 編輯備課包對話框組件
function EditLessonPackageDialog({ open, package: packageData, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    subject: '',
    grade: '',
    status: 'preparing'
  });
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (packageData && open) {
      setFormData({
        name: packageData.name || '',
        description: packageData.description || '',
        subject: packageData.subject || '',
        grade: packageData.grade || '',
        status: packageData.status || 'preparing'
      });
    }
  }, [packageData, open]);

  const handleInputChange = (field) => (event) => {
    const newValue = event.target.value;
    setFormData({
      ...formData,
      [field]: newValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.name.trim()) {
      alert('請輸入備課包名稱');
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.put(
        `${config.updateLessonPackage}${packageData.id}`,
        formData
      );
      
      if (response.data.success) {
        onSuccess('備課包更新成功');
        onClose();
      } else {
        alert('更新失敗');
      }
    } catch (error) {
      console.error('更新備課包錯誤:', error);
      alert('更新失敗');
    } finally {
      setLoading(false);
    }
  };

  if (!packageData) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EditIcon sx={{ color: '#2b687f' }} />
          <Typography variant="h6">編輯備課包</Typography>
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              label="備課包名稱"
              value={formData.name}
              onChange={handleInputChange('name')}
              required
              fullWidth
              placeholder="請輸入備課包名稱"
            />

            <TextField
              label="備課包描述"
              value={formData.description}
              onChange={handleInputChange('description')}
              multiline
              rows={3}
              fullWidth
              placeholder="請輸入備課包描述"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>學科</InputLabel>
                <Select
                  value={formData.subject}
                  onChange={handleInputChange('subject')}
                  label="學科"
                >
                  <MenuItem value="自然">自然</MenuItem>
                  <MenuItem value="數學">數學</MenuItem>
                  <MenuItem value="國語">國語</MenuItem>
                  <MenuItem value="社會">社會</MenuItem>
                  <MenuItem value="英語">英語</MenuItem>
                  <MenuItem value="其他">其他</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ flex: 1 }}>
                <InputLabel>年級</InputLabel>
                <Select
                  value={formData.grade}
                  onChange={handleInputChange('grade')}
                  label="年級"
                >
                  <MenuItem value="一年級">一年級</MenuItem>
                  <MenuItem value="二年級">二年級</MenuItem>
                  <MenuItem value="三年級">三年級</MenuItem>
                  <MenuItem value="四年級">四年級</MenuItem>
                  <MenuItem value="五年級">五年級</MenuItem>
                  <MenuItem value="六年級">六年級</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <FormControl>
              <InputLabel>狀態</InputLabel>
              <Select
                value={formData.status}
                onChange={handleInputChange('status')}
                label="狀態"
              >
                <MenuItem value="preparing">備課中</MenuItem>
                <MenuItem value="completed">完成</MenuItem>
             
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
          <Button type="button" onClick={onClose}>
            取消
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? '更新中...' : '更新備課包'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
